import { PushNotifications } from '@capacitor/push-notifications';

import { nativePlatform } from '../native/platform';

/**
 * BP-11 R4 and R11: asking for notifications, and learning the token.
 *
 * Three things live here and nowhere else.
 *
 * **The ask is timed, not automatic** (R11): "the API 33+ runtime prompt
 * shown only after the first booking is made, not at launch". A permission
 * dialog on the splash screen is the one a person denies without reading,
 * and Android gives no second chance — so `enablePush` is called from §6's
 * notifications screen and from the booking-confirmed screen, never from
 * the app shell.
 *
 * **The token arrives by event, not by return value.** `register()`
 * resolves as soon as the OS has been asked; FCM answers later on the
 * `registration` listener, and answers again whenever it rotates the token.
 * So the shape here is "tell me each time there is a token", and rule R4's
 * re-post on rotation is the same call as the first registration.
 *
 * **Nothing here talks to the api.** `web/src/features/messaging/devices`
 * does that. This module is the OS half, so a test of the registration
 * journey needs no handset and a test of the handset needs no server.
 */

/** R11's answers, narrowed from the plugin's own vocabulary. */
export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** The slice of the plugin this module uses; injected in tests. */
export interface PushPlugin {
  checkPermissions: typeof PushNotifications.checkPermissions;
  requestPermissions: typeof PushNotifications.requestPermissions;
  register: typeof PushNotifications.register;
  unregister: typeof PushNotifications.unregister;
  addListener: typeof PushNotifications.addListener;
}

/** What the app posts to `POST /me/devices` (R4). */
export interface Registration {
  platform: 'android' | 'ios';
  token: string;
}

/**
 * Narrow the plugin's permission state onto R11's.
 *
 * `prompt-with-rationale` is Android's "they said no once, you may explain
 * why and ask again". It is a `prompt`, not a `denied`: the screen that
 * calls this has the explanation, which is the whole point of the state.
 */
export function narrowPush(state: string): PushPermission {
  if (state === 'granted') {
    return 'granted';
  }
  return state === 'denied' ? 'denied' : 'prompt';
}

/** Whether this build can receive a push at all (R11, §6's `unsupported`). */
export function pushSupported(): boolean {
  return nativePlatform() !== null;
}

/** The current permission, asking nothing. §6's notifications screen reads it. */
export async function pushPermission(
  plugin: PushPlugin = PushNotifications,
): Promise<PushPermission> {
  if (!pushSupported()) {
    return 'unsupported';
  }
  return narrowPush((await plugin.checkPermissions()).receive);
}

/**
 * R11's prompt, then R4's registration.
 *
 * Returns the permission that resulted. The *token* does not come back from
 * here — it arrives on the listener, possibly after this resolves — so a
 * caller wires `onPushToken` first and then calls this.
 */
export async function enablePush(plugin: PushPlugin = PushNotifications): Promise<PushPermission> {
  if (!pushSupported()) {
    return 'unsupported';
  }
  const current = narrowPush((await plugin.checkPermissions()).receive);
  const granted =
    current === 'prompt' ? narrowPush((await plugin.requestPermissions()).receive) : current;
  if (granted !== 'granted') {
    return granted;
  }
  await plugin.register();
  return 'granted';
}

/**
 * R4: every token this installation is given, including the rotations.
 *
 * Returns the unsubscribe. The platform is attached here rather than at the
 * call site because it is the one fact the token alone does not carry and
 * `POST /me/devices` requires.
 */
export async function onPushToken(
  handle: (registration: Registration) => void,
  plugin: PushPlugin = PushNotifications,
): Promise<() => void> {
  const name = nativePlatform();
  if (name === null) {
    return () => undefined;
  }
  const listener = await plugin.addListener('registration', (token) => {
    handle({ platform: name, token: token.value });
  });
  return () => {
    void listener.remove();
  };
}

/**
 * R4's logout half: stop the OS sending to this installation.
 *
 * The `push_device` row is deleted separately, through the api. Both, and
 * in that order: unregistering alone leaves a row the salon would still
 * count as a reachable device, and deleting the row alone leaves the OS
 * delivering to an app that no longer has a session.
 */
export async function disablePush(plugin: PushPlugin = PushNotifications): Promise<void> {
  if (!pushSupported()) {
    return;
  }
  await plugin.unregister();
}
